import Dexie, { type Table } from 'dexie';

export type GpsQueueRow = {
  gps_scope?: string;
  id?: number;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  altitude?: number | null;
  speed?: number | null;
  heading?: number | null;
  label?: string;
  createdAt: string;
  /** Radna sesija u kojoj je točka opažena; nedostaje samo kod starih stavki. */
  session_id?: string;
  /** Klijentski ID događaja (UUID) za serversku deduplikaciju; nedostaje kod starih stavki. */
  event_id?: string;
};

export type VisitStartQueueRow = {
  offline_id: string;
  payload: Record<string, unknown>;
  createdAt: string;
};

export type VisitCompleteQueueRow = {
  id?: number;
  visit_id: string;
  payload: Record<string, unknown>;
  createdAt: string;
};

export type CacheRow = {
  key: string;
  data: unknown;
  savedAt: string;
  /** company_id:user_id:verzija-prava; nedostaje samo kod starih zapisa. */
  scope?: string;
};

class ZoMaOfflineDb extends Dexie {
  gpsQueue!: Table<GpsQueueRow, number>;
  visitStartQueue!: Table<VisitStartQueueRow, string>;
  visitCompleteQueue!: Table<VisitCompleteQueueRow, number>;
  cache!: Table<CacheRow, string>;

  constructor() {
    super('zoma_offline_v1');
    this.version(1).stores({
      gpsQueue: '++id, createdAt',
      visitStartQueue: 'offline_id, createdAt',
      visitCompleteQueue: '++id, visit_id, createdAt',
      cache: 'key',
    });
  }
}

export const offlineDb = new ZoMaOfflineDb();

export const MYDAY_CACHE_PREFIX = 'myday_';

export const CACHE_KEYS = {
  myDay: (scope: string, dateYmd: string) => `${MYDAY_CACHE_PREFIX}v2|${scope}|${dateYmd}`,
  visitsPage: 'visits_page_v1',
} as const;

export async function getCache<T>(key: string): Promise<{ data: T; savedAt: string } | null> {
  const row = await offlineDb.cache.get(key);
  if (!row) return null;
  return { data: row.data as T, savedAt: row.savedAt };
}

export async function setCache(key: string, data: unknown, scope?: string): Promise<void> {
  await offlineDb.cache.put({
    key,
    data,
    savedAt: new Date().toISOString(),
    ...(scope ? { scope } : {}),
  });
}

export async function getCacheRow(key: string): Promise<CacheRow | null> {
  return (await offlineDb.cache.get(key)) || null;
}

export async function deleteCache(key: string): Promise<void> {
  await offlineDb.cache.delete(key);
}

export async function listCacheRows(prefix: string): Promise<CacheRow[]> {
  return offlineDb.cache.where('key').startsWith(prefix).toArray();
}

export async function clearAllCache(): Promise<void> {
  await offlineDb.cache.clear();
}
